import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { RocketLaunch } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

function OneTimeFeeCard({ payment, formatPrice, currency, captionText }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid rgba(187,203,184,0.12)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Icon */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: '#c6e7ff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RocketLaunch sx={{ color: '#00658d', fontSize: 24 }} />
        </Box>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 700,
          fontSize: '1.125rem',
          mb: 1,
        }}
      >
        {payment.description}
      </Typography>

      {/* Spacer to push price to bottom */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Price */}
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 700,
          fontSize: '1.5rem',
          color: '#006e2a',
          mt: 2,
        }}
      >
        {formatPrice ? formatPrice(payment.total) : `${currency}${Math.ceil(payment.total)}`}
      </Typography>

      {/* Caption — included in subscriptions below */}
      <Typography
        sx={{
          fontFamily: BODY_FONT,
          fontSize: '0.75rem',
          color: 'text.secondary',
          mt: 0.5,
        }}
      >
        {captionText}
      </Typography>
    </Paper>
  );
}

export default function OneTimePaymentsList({ payments, currency = '$', formatPrice }) {
  const { t } = useTranslation();

  if (!payments || payments.length === 0) return null;

  const filtered = payments.filter((p) => p.total > 0);
  if (filtered.length === 0) return null;

  const captionText = t('offer.addedToPlanBelow', '↓ Added to subscriptions below');

  const count = filtered.length;
  const cols = count === 1 ? 1 : count === 2 ? 2 : count === 4 ? 2 : 3;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: count === 1 ? '1fr' : 'repeat(2, 1fr)',
          lg: `repeat(${cols}, 1fr)`,
        },
        gridAutoRows: '1fr',
        gap: 3,
        ...(count === 1 && { maxWidth: 600, mx: 'auto' }),
      }}
    >
      {filtered.map((payment, index) => (
        <OneTimeFeeCard
          key={index}
          payment={payment}
          formatPrice={formatPrice}
          currency={currency}
          captionText={captionText}
        />
      ))}
    </Box>
  );
}
